export type LeafFlags =
  | { alwaysRequired: boolean }
  | { perStore: boolean }
  | { label: string }
  | { inputType: string }
  | { helpText: string };
export type LeafField =
  | "alwaysRequired"
  | "perStore"
  | "label"
  | "inputType"
  | "helpText";
export type SaveStatus = "saving" | "saved" | "failed";
export interface SaveEntry {
  path: string;
  field: LeafField;
  status: SaveStatus;
  value: string | boolean;
  at: number;
}
export type SaveMap = Readonly<Record<string, SaveEntry>>;

export const saveKey = (path: string, field: LeafField) => `${path}|${field}`;

export const applySave = (map: SaveMap, entry: SaveEntry): SaveMap => ({
  ...map,
  [saveKey(entry.path, entry.field)]: entry,
});

export const forgetSaved = (map: SaveMap, entry: SaveEntry): SaveMap => {
  const key = saveKey(entry.path, entry.field);
  if (map[key] !== entry) return map;
  const { [key]: _gone, ...rest } = map;
  return rest;
};

const RANK: Record<SaveStatus, number> = { failed: 3, saving: 2, saved: 1 };

const entriesOf = (map: SaveMap, path: string) =>
  Object.values(map).filter((e) => e.path === path);

export const rowStatus = (map: SaveMap, path: string): SaveStatus | null =>
  entriesOf(map, path).reduce<SaveStatus | null>(
    (best, e) =>
      best === null || RANK[e.status] > RANK[best] ? e.status : best,
    null,
  );

export const fieldStatus = (
  map: SaveMap,
  path: string,
  field: LeafField,
): SaveStatus | null => map[saveKey(path, field)]?.status ?? null;

export const failedEntries = (map: SaveMap): SaveEntry[] =>
  Object.values(map)
    .filter((e) => e.status === "failed")
    .sort((a, b) => a.at - b.at);
